import React, { useState } from 'react';
import { useAuth, ROLE_LABEL, ROLE_BADGE } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogOut, Shield, Mail, Briefcase, Building2, Phone, KeyRound, UserCheck, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, logout, refreshMe } = useAuth();

  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    whatsapp_number: user?.whatsapp_number || '',
    nip: user?.nip || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);

  if (!user) return null;

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.patch('/auth/profile', profileForm);
      await refreshMe();
      toast.success('Profil berhasil diperbarui');
      setEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal memperbarui profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Konfirmasi password baru tidak cocok');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password baru minimal 8 karakter');
      return;
    }
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password', {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password berhasil diganti. Silakan gunakan password baru untuk login berikutnya.');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengganti password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader title="Profil Saya" subtitle="Informasi akun pengguna dan pengaturan keamanan PROAKTIF." />

      {/* Profile Overview Card */}
      <Card className="border-slate-200 shadow-premium">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#DC2626] to-[#FBBF24] text-white text-3xl font-extrabold flex items-center justify-center shadow-elevated">
                {user.full_name?.[0]}
              </div>
              <div>
                <h2 className="text-2xl font-extrabold" style={{ fontFamily: 'Plus Jakarta Sans' }}>{user.full_name}</h2>
                <div className="text-sm text-slate-600">{user.jabatan || 'Staf Pemkot Makassar'}</div>
                <div className={cn('inline-block mt-1 px-2.5 py-0.5 rounded text-xs font-semibold', ROLE_BADGE[user.role])}>
                  {ROLE_LABEL[user.role]}
                </div>
              </div>
            </div>
            <Button
              variant={editingProfile ? 'outline' : 'default'}
              className={!editingProfile ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              onClick={() => {
                setEditingProfile(!editingProfile);
                setProfileForm({
                  full_name: user?.full_name || '',
                  phone: user?.phone || '',
                  whatsapp_number: user?.whatsapp_number || '',
                  nip: user?.nip || '',
                });
              }}
            >
              <UserCheck className="w-4 h-4 mr-2" />
              {editingProfile ? 'Batal Edit' : 'Edit Profil'}
            </Button>
          </div>

          {editingProfile ? (
            <form onSubmit={handleUpdateProfile} className="mt-6 pt-6 border-t border-slate-100 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Edit Data Profil</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">Nama Lengkap</Label>
                  <Input
                    id="full_name"
                    value={profileForm.full_name}
                    onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="nip">NIP</Label>
                  <Input
                    id="nip"
                    value={profileForm.nip}
                    onChange={(e) => setProfileForm({ ...profileForm, nip: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Nomor Telepon</Label>
                  <Input
                    id="phone"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="whatsapp_number">Nomor WhatsApp Aktif</Label>
                  <Input
                    id="whatsapp_number"
                    placeholder="Contoh: 08123456789 atau +628123456789"
                    value={profileForm.whatsapp_number}
                    onChange={(e) => setProfileForm({ ...profileForm, whatsapp_number: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditingProfile(false)}>Batal</Button>
                <Button type="submit" disabled={savingProfile} className="bg-red-600 hover:bg-red-700 text-white">
                  {savingProfile && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Simpan Perubahan
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-6 grid md:grid-cols-2 gap-3">
              <InfoBox icon={Mail} label="Email Akun" value={user.email} />
              <InfoBox icon={Shield} label="NIP" value={user.nip || '-'} />
              <InfoBox icon={Building2} label="OPD" value={user.opd_nama || '-'} />
              <InfoBox icon={Briefcase} label="Jabatan" value={user.jabatan || '-'} />
              <InfoBox icon={Phone} label="Telepon" value={user.phone || '-'} />
              <InfoBox icon={Phone} label="WhatsApp" value={user.whatsapp_number || '-'} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card className="border-slate-200 shadow-premium">
        <CardHeader>
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-red-600" />
            <CardTitle className="text-lg">Ganti Password</CardTitle>
          </div>
          <CardDescription>
            Gunakan kombinasi minimal 8 karakter untuk menjaga keamanan akun Anda.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-xl">
            <div className="space-y-1.5">
              <Label htmlFor="old_password">Password Lama</Label>
              <Input
                id="old_password"
                type="password"
                placeholder="Masukkan password saat ini"
                value={passwordForm.old_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new_password">Password Baru</Label>
              <Input
                id="new_password"
                type="password"
                placeholder="Minimal 8 karakter"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm_password">Konfirmasi Password Baru</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="Ulangi password baru"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
              />
            </div>
            <Button type="submit" disabled={savingPassword} className="bg-red-600 hover:bg-red-700 text-white">
              {savingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Perbarui Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Logout Action */}
      <div className="flex justify-end pt-2">
        <Button variant="destructive" onClick={logout}>
          <LogOut className="w-4 h-4 mr-2" /> Keluar dari Sistem
        </Button>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="p-3.5 rounded-lg border border-slate-200 bg-slate-50/50">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        <Icon className="w-3.5 h-3.5 text-slate-400" /> {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 mt-1">{value}</div>
    </div>
  );
}
